/* eslint-disable max-lines */
import { BottomSheet, BottomSheetIllustrationContent, Text } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { logout } from "@src/app/store/session/actions";
import { APP_V2_USER_EVENTS, logError } from "@src/appV2/lib/analytics";
import type { Worker } from "@src/appV2/Worker/api/types";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { useSelectedWorkType } from "../hooks/useSelectedWorkType";
import { OnboardingPersonalIdVerificationContainer } from "../PersonalIdVerification/Container";
import { ONBOARDING_STAGES, ONBOARDING_STAGES_PATHS } from "../util/constants";
import { SHOULD_SHOW_EDIT_ADDRESS_QUERY_PARAM } from "./AddressStep/constants";
import { AddressPage } from "./AddressStep/Page";
import { EmailStep } from "./EmailStep/EmailStep";
import { ICAgreementStep } from "./ICAgreementStep/ICAgreementStep";
import { InterestedWorkTypeStep } from "./InterestedWorkTypeStep/InterestedWorkTypeStep";
import { WorkTypeOption } from "./InterestedWorkTypeStep/InterestedWorkTypeStepContent";
import { useAskInterestedWorkType } from "./InterestedWorkTypeStep/useAskInterestedWorkType";
import { NamePage } from "./NameStep/Page";
import { RoleStep } from "./RolesStep/RoleStep";
import { type Step } from "./types";
import { useResumeFromAbandonedStep } from "./useResumeFromAbandonedStep";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";

interface Props {
  readonly worker: Worker | undefined;
}

function calculateProgress(pathname: string, steps: Step[]) {
  const currentStep = steps.findIndex((step) => step.route === pathname);
  return {
    current: currentStep + 1,
    total: steps.length,
  };
}

export function OnboardingFlowContainer(props: Props) {
  const { worker } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { selectedWorkType: interestedWorkType } = useSelectedWorkType();

  const { data: shouldAskInterestedWorkType, refetch: fetchShouldAskInterestedWorkType } =
    useAskInterestedWorkType({
      workerId: worker?.userId ?? "",
    });

  // if no interested work type is selected, we consider it as flex work type
  const isFlexWorkTypeSelected =
    !interestedWorkType ||
    interestedWorkType === WorkTypeOption.FLEX ||
    interestedWorkType === WorkTypeOption.PERMANENT_AND_FLEX;

  const steps: Step[] = [
    {
      stage: ONBOARDING_STAGES.EMAIL,
      route: ONBOARDING_STAGES_PATHS.EMAIL,
      render: ({ progress }) => (
        <EmailStep
          stage={ONBOARDING_STAGES.EMAIL}
          worker={worker}
          progress={progress}
          onBack={() => {
            dispatch(logout());
          }}
          onNext={() => {
            history.push(ONBOARDING_STAGES_PATHS.NAME);
          }}
        />
      ),
    },
    {
      stage: ONBOARDING_STAGES.NAME,
      route: ONBOARDING_STAGES_PATHS.NAME,
      render: ({ progress }) => (
        <NamePage
          stage={ONBOARDING_STAGES.NAME}
          worker={worker}
          progress={progress}
          onBack={() => {
            history.replace(ONBOARDING_STAGES_PATHS.EMAIL);
          }}
          onNext={() => {
            history.push(ONBOARDING_STAGES_PATHS.ADDRESS);
          }}
        />
      ),
    },
    {
      stage: ONBOARDING_STAGES.ADDRESS,
      route: ONBOARDING_STAGES_PATHS.ADDRESS,
      render: ({ progress }) => {
        return (
          <AddressPage
            stage={ONBOARDING_STAGES.ADDRESS}
            worker={worker}
            progress={progress}
            onBack={() => {
              history.replace(ONBOARDING_STAGES_PATHS.NAME);
            }}
            onNext={async () => {
              const {
                data: shouldAskInterestedWorkType,
                isError,
                error,
              } = await fetchShouldAskInterestedWorkType();

              if (isError && error) {
                logError(APP_V2_USER_EVENTS.ASK_INTERESTED_WORK_TYPE_ERROR, {
                  error,
                  metadata: {
                    title: "Error fetching should ask interested work type",
                  },
                });
              }

              if (shouldAskInterestedWorkType && !isError && !error) {
                history.push(ONBOARDING_STAGES_PATHS.INTERESTED_WORK_TYPE);
              } else {
                history.push(ONBOARDING_STAGES_PATHS.LICENSE);
              }
            }}
          />
        );
      },
    },
    {
      stage: ONBOARDING_STAGES.INTERESTED_WORK_TYPE,
      route: ONBOARDING_STAGES_PATHS.INTERESTED_WORK_TYPE,
      render: ({ progress }) => {
        return (
          <InterestedWorkTypeStep
            stage={ONBOARDING_STAGES.INTERESTED_WORK_TYPE}
            worker={worker}
            progress={progress}
            onBack={() => {
              history.replace(
                `${ONBOARDING_STAGES_PATHS.ADDRESS}?${SHOULD_SHOW_EDIT_ADDRESS_QUERY_PARAM}=true`
              );
            }}
            onNext={() => {
              history.push(ONBOARDING_STAGES_PATHS.LICENSE);
            }}
          />
        );
      },
    },
    {
      // Historically, license stage is used for role selection screen
      // We are continuing to use license stage here for the new roles too
      stage: ONBOARDING_STAGES.LICENSE,
      route: ONBOARDING_STAGES_PATHS.LICENSE,
      render: ({ progress }) => {
        return (
          <RoleStep
            stage={ONBOARDING_STAGES.LICENSE}
            worker={worker}
            progress={progress}
            onBack={async () => {
              if (shouldAskInterestedWorkType) {
                history.replace(ONBOARDING_STAGES_PATHS.INTERESTED_WORK_TYPE);
              } else {
                history.replace(
                  `${ONBOARDING_STAGES_PATHS.ADDRESS}?${SHOULD_SHOW_EDIT_ADDRESS_QUERY_PARAM}=true`
                );
              }
            }}
            onNext={() => {
              if (isFlexWorkTypeSelected) {
                history.push(ONBOARDING_STAGES_PATHS.PERSONAL_ID_VERIFICATION);
              } else {
                history.push(ONBOARDING_STAGES_PATHS.FINAL);
              }
            }}
          />
        );
      },
    },
    {
      stage: ONBOARDING_STAGES.PERSONAL_ID_VERIFICATION,
      route: ONBOARDING_STAGES_PATHS.PERSONAL_ID_VERIFICATION,
      render: ({ progress }) => {
        return (
          <OnboardingPersonalIdVerificationContainer
            stage={ONBOARDING_STAGES.PERSONAL_ID_VERIFICATION}
            progress={progress}
            worker={worker}
            onBack={() => {
              history.replace(ONBOARDING_STAGES_PATHS.LICENSE);
            }}
            onNext={() => {
              if (isFlexWorkTypeSelected) {
                history.push(ONBOARDING_STAGES_PATHS.CONTRACTOR_AGREEMENT);
              } else {
                history.push(ONBOARDING_STAGES_PATHS.FINAL);
              }
            }}
          />
        );
      },
    },
    {
      stage: ONBOARDING_STAGES.CONTRACTOR_AGREEMENT,
      route: ONBOARDING_STAGES_PATHS.CONTRACTOR_AGREEMENT,
      render: ({ progress }) => {
        return (
          <ICAgreementStep
            stage={ONBOARDING_STAGES.CONTRACTOR_AGREEMENT}
            progress={progress}
            worker={worker}
            onBack={() => {
              history.replace(ONBOARDING_STAGES_PATHS.PERSONAL_ID_VERIFICATION);
            }}
            onNext={() => {
              history.push(ONBOARDING_STAGES_PATHS.FINAL);
            }}
          />
        );
      },
    },
    {
      stage: ONBOARDING_STAGES.FINAL,
      route: ONBOARDING_STAGES_PATHS.FINAL,
      render: () => {
        return <WelcomeStep worker={worker} />;
      },
    },
  ];

  const { modalState: resumeFromAbandonedStepModalState } = useResumeFromAbandonedStep({
    steps,
    worker,
  });

  if (resumeFromAbandonedStepModalState.modalIsOpen) {
    return (
      <ThemeProvider theme={getTheme()}>
        <BottomSheet
          modalState={resumeFromAbandonedStepModalState}
          paperSx={(theme) => ({
            ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
          })}
          header={<BottomSheetHeader onClose={resumeFromAbandonedStepModalState.closeModal} />}
          footer={
            <DialogFooter orientation="vertical">
              <Button
                fullWidth
                size="large"
                onClick={() => {
                  resumeFromAbandonedStepModalState.closeModal();
                }}
              >
                Continue Sign Up
              </Button>
            </DialogFooter>
          }
        >
          <BottomSheetIllustrationContent
            illustrationType="complete-profile"
            title="Welcome back!"
            description={
              <Text>
                Pick up where you left off &mdash; You&apos;re few steps away from creating your
                Clipboard account.
              </Text>
            }
          />
        </BottomSheet>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <Switch>
        {steps.map((step) => (
          <Route
            key={step.stage}
            exact
            path={step.route}
            render={({ match }) => {
              return step.render({ progress: calculateProgress(match.path, steps) });
            }}
          />
        ))}
        {/* Redirect to first step if no step is matched */}
        <Route>
          {() => {
            return <Redirect to={steps[0].route} />;
          }}
        </Route>
      </Switch>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
